import { useEffect, useRef, useState } from 'react'
import { auroraStore, useAuroraStore } from '../../store/auroraStore'
import { activeChild } from '../../store/selectors'
import { pushToast } from '../../lib/toast'

/**
 * The open door at the top of the day.
 *
 * Anything on the parent's mind goes here, in their words. A statement is
 * logged as an observation (the highest-value data in the system); a question
 * goes to the thread and gets an answer. The mic is the same ten-second
 * capture as always, just where the eye already is.
 */
export function TellAurora() {
  const child = useAuroraStore(activeChild) as any
  const [text, setText] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey) return
      const t = e.target as HTMLElement
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return
      e.preventDefault()
      inputRef.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  if (!child) return null
  const name = child.name ?? 'her'

  const log = () => {
    const t = text.trim()
    if (!t) return
    ;(auroraStore.getState() as any).logTextCapture({ childId: child._id, text: t })
    setText('')
    pushToast(`Noted — this becomes part of ${name}'s record`)
  }

  const ask = () => {
    const t = text.trim()
    if (!t) return
    const s = auroraStore.getState() as any
    s.setComposing(t)
    s.setView('thread')
    setText('')
  }

  const looksLikeQuestion = /\?\s*$|^(what|how|why|when|should|can|could|is|are|do|does|any)\b/i.test(text.trim())

  return (
    <div className="tell reveal" style={{ '--i': 1 } as React.CSSProperties}>
      <button
        type="button"
        className="tell-mic"
        onClick={() => (auroraStore.getState() as any).startRecording()}
        aria-label="Hold to talk instead"
        title="Talk instead"
      >
        <MicIcon />
      </button>
      <input
        ref={inputRef}
        className="tell-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') (e.target as HTMLInputElement).blur()
          if (e.key !== 'Enter') return
          if (looksLikeQuestion) ask()
          else log()
        }}
        placeholder={`What did ${name} do or say? Or ask anything…`}
        aria-label="Tell Aurora something or ask a question"
      />
      {text.trim() && (
        <span className="tell-actions">
          <button className={`btn btn-sm ${looksLikeQuestion ? 'btn-quiet' : 'btn-primary'}`} onClick={log}>
            Log it
          </button>
          <button className={`btn btn-sm ${looksLikeQuestion ? 'btn-primary' : 'btn-quiet'}`} onClick={ask}>
            Ask
          </button>
        </span>
      )}
    </div>
  )
}

const MicIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="2" width="6" height="12" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0M12 18v4" />
  </svg>
)
